import { Payout } from 'store/api/payout';
import { Role, PayoutType, PayoutStatus } from 'types';
import {
  Flex,
  RemovePayoutDialog,
  EditDecisionDialog,
  ApprovalPayoutDialog,
} from 'components';
import {
  editDecisionStatuses,
  approveReimbursementRoles,
  canDeleteReimbursementStatuses,
} from 'config';

export interface GetRightSlotApprovalDialog {
  data?: Payout;
  type: PayoutType;
  authUserRole?: Role;
  // the page's "edit the item" action, when the item can be edited
  onEdit?: () => void;
  withNavigate?: boolean;
  isCompanyOwner?: boolean;
}

const getDecisionRights = ({
  data,
  authUserRole,
  isCompanyOwner,
}: GetRightSlotApprovalDialog) => {
  const canApproveReimbursement =
    !!isCompanyOwner ||
    (!!authUserRole && approveReimbursementRoles.includes(authUserRole));

  // Owner 19 Oct: a pending item gets Approve / Partial / Reject; an
  // approved, partially approved or rejected item that is not on a paycheck
  // yet gets one edit icon to change the decision.
  const isUnpaid = !!data && !data.payout?.id;
  const canEditDecision =
    isUnpaid && !!data && editDecisionStatuses.includes(data.status);

  return { canEditDecision, canApproveReimbursement };
};

export const getRightSlotApprovalDialog = ({
  data,
  type,
  onEdit,
  authUserRole,
  isCompanyOwner,
  withNavigate = true,
}: GetRightSlotApprovalDialog) => {
  const { canEditDecision, canApproveReimbursement } = getDecisionRights({
    data,
    type,
    authUserRole,
    isCompanyOwner,
  });
  const isPending = data?.status === PayoutStatus.Pending;
  const canDelete =
    !!data && canDeleteReimbursementStatuses.includes(data.status);

  return data &&
    canApproveReimbursement &&
    (isPending || canEditDecision || canDelete) ? (
    <Flex gap={8}>
      {isPending && (
        <ApprovalPayoutDialog
          data={data}
          type={type}
          withNavigate={withNavigate}
        />
      )}
      {canEditDecision && (
        <EditDecisionDialog
          data={data}
          type={type}
          onEditItem={onEdit}
          withNavigate={withNavigate}
        />
      )}
      {canDelete && <RemovePayoutDialog data={data} />}
    </Flex>
  ) : undefined;
};

/**
 * Details page header: the right slot plus the page's own edit action. When
 * the decision can be changed, the item's edit moves into the decision
 * pencil's menu (Edit item / Edit decision), so the page never shows two
 * pencils (owner 19 Oct); otherwise the page keeps its own edit button.
 */
export const getPayoutDetailsActions = (args: GetRightSlotApprovalDialog) => {
  const { canEditDecision, canApproveReimbursement } = getDecisionRights(args);
  const merged = !!args.onEdit && canEditDecision && canApproveReimbursement;

  return {
    onEdit: merged ? undefined : args.onEdit,
    rightSlot: getRightSlotApprovalDialog(args),
  };
};
